import { Box, Input, Space, Stack, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import {
	SettingsBlock,
	SettingsButton,
	SettingsTextInput,
} from '@repo/common/components';
import { Button, Text } from '@repo/foundations';
import { useDebounceFn } from 'ahooks';
import { useCallback, useRef, useState } from 'react';
import { useAuthUser } from '../../api';
import { useUpdateWorkspace } from '../../api/hooks/workspace/useUpdateWorkspace';
import { useWorkspaceImage } from '../../api/hooks/workspace/useWorkspaceImage';
import { validateWorkspaceName } from '../../utils/workspaceValidation';
import { openModal } from '../ModalManager';
import { SettingsWorkspaceImageUpload } from './SettingsWorkspaceImageUpload';

function WorkspaceForm() {
	const { workspace } = useAuthUser();
	const { mutateAsync } = useUpdateWorkspace(workspace.id);
	const { mutateAsync: uploadImage } = useWorkspaceImage();

	const [workspaceName, setWorkspaceName] = useState(workspace.name);
	const [nameError, setNameError] = useState<string | null>(null);

	const onUpload = useCallback(
		async (file: File | null) => {
			if (!file) return;
			await uploadImage({
				workspaceId: workspace.id,
				file,
			});
			showNotification({
				title: 'Workspace logo updated',
				message: 'The workspace logo has been updated',
				color: 'green',
			});
		},
		[uploadImage, workspace.id]
	);

	const { run: updateWorkspace } = useDebounceFn(
		async (name: string) => {
			const validationError = validateWorkspaceName(name);
			if (validationError) {
				setNameError(validationError);
				return;
			}

			setNameError(null);
			try {
				await mutateAsync({
					data: {
						id: workspace.id,
						name,
					},
				});
				showNotification({
					title: 'Workspace name updated',
					message: 'The workspace name has been updated',
					color: 'green',
				});
			} catch {
				showNotification({
					title: 'Error updating workspace name',
					message: 'Failed to update workspace name. Please try again.',
					color: 'red',
				});
			}
		},
		{
			wait: 1000,
		}
	);

	return (
		<Box>
			<SettingsWorkspaceImageUpload
				title="Logo"
				onChange={onUpload}
				imageUrl={workspace.logo}
			/>
			<SettingsTextInput
				title="Name"
				value={workspaceName}
				error={nameError}
				onChange={(value) => {
					setWorkspaceName(value);
					// Clear error immediately when user starts typing
					if (nameError) {
						setNameError(null);
					}
					updateWorkspace(value);
				}}
			/>
		</Box>
	);
}

interface IWorkspaceNameCheckModalProps {
	callback: () => void;
	workspace: { name: string };
}

function WorkspaceNameCheckModal({
	callback,
	workspace,
}: IWorkspaceNameCheckModalProps) {
	const theme = useMantineTheme();

	const ref = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string | null>(null);

	const handleCallback = () => {
		if (ref.current?.value !== workspace.name) {
			setError('The workspace name does not match');
			return;
		}
		callback();
	};

	return (
		<Box>
			<Text>Enter the current workspace name:</Text>
			<Input
				data-testid="workspace-name-delete-input"
				error={error}
				ref={ref}
				placeholder={workspace.name}
			/>
			<Space my={theme.spacing.md} />
			<Button
				data-testid="delete-workspace-modal"
				variant="primary"
				tone="critical"
				onClick={handleCallback}
			>
				Delete workspace
			</Button>
		</Box>
	);
}

export function WorkspaceSettings() {
	const { isAdminUser, workspace } = useAuthUser();
	const { data: memberEnv } = useSuspenseApiMemberEnv({});
	const theme = useMantineTheme();

	const { mutateAsync } = useUpdateWorkspace(workspace.id);

	const handleDeleteWorkspaceModal = useCallback(() => {
		const handleDelete = async () => {
			await mutateAsync({
				data: {
					id: workspace.id,
					disabled: true,
					scheduled_for_deletion: true,
				},
			});
			window.location.reload();
		};

		openModal({
			title: 'Are you sure you want to permanently delete this workspace?',
			children: (
				<WorkspaceNameCheckModal
					callback={handleDelete}
					workspace={workspace}
				/>
			),
		});
	}, [mutateAsync, workspace]);

	return (
		<Box>
			<WorkspaceForm />

			<Stack my={theme.spacing.xl}>
				<Box>
					<SettingsBlock
						title="Version"
						description={`${memberEnv?.TAG} ${memberEnv?.COMMIT_TAG}`}
					/>
				</Box>
				<Box>
					{isAdminUser && (
						<SettingsButton
							title="Delete workspace"
							description="Disable the current workspace and queue it for deletion"
							buttonText="Delete workspace"
							onClick={handleDeleteWorkspaceModal}
						/>
					)}
				</Box>
			</Stack>
		</Box>
	);
}
